import { useEffect, useState } from 'react'
import { useAuthStore } from '../stores/auth'
import * as api from '../api'
import {
  Briefcase, LogOut, Plus, RefreshCw, ExternalLink, AlertTriangle,
  Settings, Edit3, Trash2,
} from 'lucide-react'
import ClientFormDialog from '../components/ClientFormDialog'

interface CardData {
  client: api.Client
  summary?: api.ClientSummary
  loading: boolean
  error?: string
}

const fmtYen = (n: number) =>
  '¥' + Math.round(n).toLocaleString()

export default function DashboardPage() {
  const { user, logout } = useAuthStore()
  const [cards, setCards] = useState<CardData[]>([])
  const [showSettings, setShowSettings] = useState(false)
  const [editTarget, setEditTarget] = useState<api.Client | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const loadAll = async () => {
    try {
      const { clients } = await api.clients.list()
      setCards(clients.map((c) => ({ client: c, loading: true })))
      // 並列でサマリー取得
      await Promise.all(clients.map(async (c, i) => {
        try {
          const s = await api.clients.summary(c.id)
          setCards((prev) => prev.map((p, idx) => idx === i ? { ...p, summary: s, loading: false } : p))
        } catch (e) {
          setCards((prev) => prev.map((p, idx) => idx === i ? { ...p, error: e instanceof Error ? e.message : '取得失敗', loading: false } : p))
        }
      }))
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => { loadAll() }, [])

  const openClient = async (id: string) => {
    try {
      const r = await api.clients.sso(id)
      window.location.href = r.ssoUrl
    } catch (e) {
      alert('SSO遷移に失敗しました: ' + (e instanceof Error ? e.message : ''))
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`「${name}」を顧問先一覧から削除しますか？\n（顧問先側のデータには影響しません）`)) return
    try {
      await api.clients.remove(id)
      await loadAll()
    } catch (e) {
      alert(e instanceof Error ? e.message : '削除失敗')
    }
  }

  return (
    <div className="min-h-screen">
      {/* ヘッダー */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-blue-600" />
            <h1 className="text-lg font-bold text-gray-900">アドバイザーポータル</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">{user?.name}（{user?.email}）</span>
            <button onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition ${showSettings ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
              title="顧問先管理">
              <Settings className="w-4 h-4 text-gray-600" />
            </button>
            <button onClick={loadAll} className="p-2 hover:bg-gray-100 rounded-lg transition" title="再読み込み">
              <RefreshCw className="w-4 h-4 text-gray-600" />
            </button>
            <button onClick={() => logout()} className="p-2 hover:bg-gray-100 rounded-lg transition" title="ログアウト">
              <LogOut className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      {/* 本体 */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">顧問先一覧（{cards.length}件）</h2>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition">
            <Plus className="w-4 h-4" />顧問先を追加
          </button>
        </div>

        {cards.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-500 text-sm">
            顧問先がまだ登録されていません。<br />
            右上の「顧問先を追加」から、各社の URL とアドバイザートークンを登録してください。
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map(({ client, summary, loading, error }) => (
              <div key={client.id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition cursor-pointer relative group"
                onClick={() => !showSettings && openClient(client.id)}>
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-gray-900 truncate">{client.name}</h3>
                    {client.memo && <p className="text-[11px] text-gray-500 truncate mt-0.5">{client.memo}</p>}
                  </div>
                  {showSettings ? (
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setEditTarget(client) }}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title="編集">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(client.id, client.name) }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="削除">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                  )}
                </div>

                {loading ? (
                  <div className="text-xs text-gray-400 py-4 text-center">読込中...</div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />{error}
                  </div>
                ) : summary ? (
                  <div className="space-y-2">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-[10px] text-blue-700 font-medium uppercase tracking-wide">現在残高</div>
                      <div className="text-xl font-bold text-blue-900 tabular-nums mt-0.5">
                        {fmtYen(summary.balance)}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-green-50 rounded-lg p-2">
                        <div className="text-[10px] text-green-700 font-medium">今月の収入</div>
                        <div className="text-sm font-bold text-green-900 tabular-nums">{fmtYen(summary.monthIncome)}</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2">
                        <div className="text-[10px] text-red-700 font-medium">今月の支出</div>
                        <div className="text-sm font-bold text-red-900 tabular-nums">{fmtYen(summary.monthExpense)}</div>
                      </div>
                    </div>
                    <div className={`rounded-lg p-2 ${summary.monthDiff >= 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      <div className={`text-[10px] font-medium ${summary.monthDiff >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>今月の差引</div>
                      <div className={`text-sm font-bold tabular-nums ${summary.monthDiff >= 0 ? 'text-emerald-900' : 'text-amber-900'}`}>
                        {summary.monthDiff >= 0 ? '+' : ''}{fmtYen(summary.monthDiff)}
                      </div>
                    </div>
                    {summary.dangerDate && (
                      <div className="bg-red-100 border border-red-300 rounded-lg p-2 text-xs text-red-900 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold">資金ショート予測</div>
                          <div className="text-[11px]">
                            {new Date(summary.dangerDate).toLocaleDateString('ja-JP')} に
                            {summary.dangerBalance != null ? ` ${fmtYen(summary.dangerBalance)}` : ''}
                          </div>
                        </div>
                      </div>
                    )}
                    {summary.updatedAt && (
                      <div className="text-[10px] text-gray-400 pt-1 text-right">
                        最終更新: {new Date(summary.updatedAt).toLocaleString('ja-JP')}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </main>

      {showCreate && (
        <ClientFormDialog
          target={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); loadAll() }}
        />
      )}
      {editTarget && (
        <ClientFormDialog
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); loadAll() }}
        />
      )}
    </div>
  )
}
